// import { LoginByEmailResponse } from '~/types'
import { equals } from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import hidePasswordIcon from '~/assets/icons-light-theme/icon-hidePassword-dark900.svg'
import showPasswordIcon from '~/assets/icons-light-theme/icon-showPassword-dark900.svg'
import iconWarningCautionRed from '~/assets/icons-light-theme/icon-warning-caution-red.svg'
import mangoLogo from '~/assets/mango-gradientLogo_std.png'
import { setAppSessionJwts } from '~/atoms/AuthenticationState'
import { Alert, FontStyle, ImageButton, Input, Text } from '~/components/core'
import { MangoButton } from '~/components/shared/MangoButton'
import { light } from '~/constants/theme'
import urls from '~/constants/urls'
import { useMouseEvents } from '~/hooks/useMouseEvents'
import { LoginEnvironmentToggle } from '~/pages/Login/LoginEnvironmentToggle'
import { useLoginWithPassword } from '~/queries/loginQueries'
import { LoginByEmailResponse } from '~/types'
import { Size } from '~/types/ThemeTypes'
import { detectAutofillAsync } from '~/utils/browser'
import { validateEmail } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'
import { BottomToastError } from '~/utils/toast'

import { ApiError } from '@mangovoice/types'

import {
  ContentWrapper,
  ErrorWrapper,
  ForgotButton,
  ForgotButtonWrapper,
  InputWrapper,
  LoginButtonWrapper,
  LogoImage,
  PassButtonWrapper,
} from './Login.styles'

interface LoginPromptProps {
  onLoginSuccess?: () => void
}

export const LoginPrompt = ({ onLoginSuccess }: LoginPromptProps) => {
  const [username, setUsername] = useState('')
  const [errUsername, setErrUsername] = useState('')
  const [autoFilled, setAutoFilled] = useState(false)
  const [password, setPassword] = useState('')
  const [errPassword, setErrPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const usernameInput = useRef<HTMLInputElement>()
  const passwordInput = useRef<HTMLInputElement>()

  const getAutoFillStatus = async () => {
    const usernameAutofillPromise = detectAutofillAsync(
      usernameInput.current!,
      300,
    )
    const passwordAutofillPromise = detectAutofillAsync(
      passwordInput.current!,
      300,
    )

    const results = await Promise.all([
      usernameAutofillPromise,
      passwordAutofillPromise,
    ])
    if (!results.every((r) => r === true)) {
      return
    }

    setAutoFilled(true)
  }

  const isMouseDown = useMouseEvents()

  //on mount, attempt to read autofill data from browser without user interaction
  useEffect(() => {
    getAutoFillStatus()
  }, [])

  useEffect(() => {
    if (isMouseDown) {
      if (autoFilled) {
        //attempt to read the input values
        const username = usernameInput.current?.value
        const password = passwordInput.current?.value
        setUsername(username ?? '')
        setPassword(password ?? '')
        setAutoFilled(false)
      }
    }
  }, [isMouseDown])

  const onLoginSuccessInternal = (data: LoginByEmailResponse | ApiError) => {
    if (data instanceof Error) {
      BottomToastError('Login failed. Please try again.')
      return
    }

    setAppSessionJwts({
      access_token: (data as LoginByEmailResponse).access,
      refresh_token: (data as LoginByEmailResponse).refresh,
    })
    onLoginSuccess?.()
  }

  const {
    refetch: loginRefetch,
    isError,
    isFetching,
  } = useLoginWithPassword(onLoginSuccessInternal)

  const login = () => {
    window.gtag('event', 'login_request', {})
    loginRefetch(username, password)
    setIsSubmitted(true)
  }

  const handleLogin = () => {
    if (username && password) {
      if (!validateEmail(username)) {
        setErrUsername('Invalid email address!')
      } else {
        login()
      }
    } else {
      if (!username) {
        setErrUsername('Please enter username')
      }
      if (!password) {
        setErrPassword('Please enter a password')
      }
    }
  }

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault()
    openInNewTab(urls.FORGOT)
  }

  const onChangeUsername = (changedUsername) => {
    setUsername(changedUsername)
    setIsSubmitted(false)
    setErrUsername('')
  }

  const onChangePassword = (changedPassword) => {
    setPassword(changedPassword)
    setIsSubmitted(false)
    setErrPassword('')
  }

  const togglePasswordObfuscate = () => {
    setShowPassword(!showPassword)
  }

  const handleKeydownEvent = (el) => (e) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      if (equals(el, 'username')) {
        passwordInput.current?.focus()
      }
      if (equals(el, 'password')) {
        handleLogin()
      }
    }
  }

  const errorMsg =
    'Invalid username or password, please check your credentials and try again.'

  const disabled =
    !autoFilled && (username === '' || password === '' || isSubmitted)

  return (
    <>
      <ToastContainer autoClose={2000} pauseOnFocusLoss={false} />
      <ContentWrapper>
        <LogoImage alt="logo" src={mangoLogo} />
        {isError && (
          <ErrorWrapper>
            <Alert icon={iconWarningCautionRed}>{errorMsg}</Alert>
          </ErrorWrapper>
        )}
        {errUsername !== '' && (
          <ErrorWrapper>
            <Text
              fontStyle={FontStyle.bSmall}
              text={errUsername}
              color={light.red200}
            />
          </ErrorWrapper>
        )}
        {errPassword !== '' && (
          <ErrorWrapper>
            <Text
              fontStyle={FontStyle.bSmall}
              text={errPassword}
              color={light.red200}
            />
          </ErrorWrapper>
        )}
        <form>
          <InputWrapper>
            <Input
              // @ts-expect-error - TODO: fix this
              id="username-input"
              placeholder="email / username"
              type="email"
              name="email"
              ref={usernameInput}
              value={username}
              onChange={onChangeUsername}
              onKeyDown={handleKeydownEvent('username')}
            />
          </InputWrapper>
          <InputWrapper>
            <Input
              // @ts-expect-error - TODO: fix this
              id="password-input"
              placeholder="password"
              name="password"
              ref={passwordInput}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={onChangePassword}
              onKeyDown={handleKeydownEvent('password')}
            />
            <PassButtonWrapper>
              <ImageButton
                style={{ marginTop: '1px' }}
                icon={showPassword ? hidePasswordIcon : showPasswordIcon}
                width="24px"
                height="36px"
                imgWidth="20"
                imgHeight="20"
                backColor={light.transparent}
                activeColor={light.transparent}
                hoveredColor={light.transparent}
                disabledColor={light.transparent}
                onClick={togglePasswordObfuscate}
              />
            </PassButtonWrapper>
          </InputWrapper>
          <LoginButtonWrapper>
            <MangoButton
              onClick={handleLogin}
              disabled={disabled || isFetching}
              size={Size.md}
            >
              Log In
            </MangoButton>
          </LoginButtonWrapper>
        </form>
        <ForgotButtonWrapper>
          <ForgotButton onClick={handleForgotPassword}>
            <Text
              fontStyle={FontStyle.bSmall}
              text="Forgot your password?"
              color={light.primary}
            />
          </ForgotButton>
        </ForgotButtonWrapper>
        <LoginEnvironmentToggle />
      </ContentWrapper>
    </>
  )
}
